import React from "react";
//#region types
import { FileViewerInnerProps, FileViewerImageState } from "models/propTypes/fileViewerPropTypes";
import { FileEnums } from "models/enums";
import { isHeicOrHeif } from "utils/helpers/heicToJpegHelper";
//#endregion
//#region components
import NoImage from "images/no_image.png";
import { FileViewerContext } from "components/common/fileViewer/context";
//#endregion

interface FileViewerImageComponentState extends FileViewerImageState {
  convertedData?: string;
  convertedContentType?: string;
  isConverting: boolean;
}

class FileViewerImage extends React.Component<FileViewerInnerProps, FileViewerImageComponentState> {
  static contextType = FileViewerContext;
  context!: React.ContextType<typeof FileViewerContext>;

  mounted: boolean = true;

  constructor(props: FileViewerInnerProps) {
    super(props);

    this.state = {
      currentWidth: 0,
      currentHeight: 0,
      isConverting: false,
    };
  }

  componentDidMount() {
    this.mounted = true;

    this.convertHeicIfNeeded();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps: FileViewerInnerProps) {
    if (
      this.props.currentPage !== prevProps.currentPage ||
      this.props.currentFile.data !== prevProps.currentFile.data ||
      this.props.currentFile.contentType !== prevProps.currentFile.contentType ||
      this.props.containerWidth !== prevProps.containerWidth ||
      this.props.containerHeight !== prevProps.containerHeight
    ) {
      this.convertHeicIfNeeded();
    }
  }

  convertHeicIfNeeded = async () => {
    const { contentType, data } = this.props.currentFile;

    if (!data) {
      this.setImageWidthHeight();
      return;
    }

    // Check if HEIC/HEIF conversion is needed
    const isHeic = isHeicOrHeif(contentType);
    if (!isHeic) {
      // Clear converted state if switching from HEIC to non-HEIC file
      if (this.state.convertedData || this.state.convertedContentType || this.state.isConverting) {
        this.setState({
          convertedData: undefined,
          convertedContentType: undefined,
          isConverting: false,
        },
          () => {
            if (this.mounted) {
              this.setImageWidthHeight();
            }
          }
        );
        return;
      }
      this.setImageWidthHeight();
      return;
    }

    // Reset conversion state if file changed (allows retry on prop updates)
    // Since componentDidUpdate only calls this when data/contentType changes,
    // reset isConverting to allow retry if previous attempt failed or was interrupted
    if (this.state.isConverting && !this.state.convertedData) {
      // Previous conversion attempt may have failed or been interrupted - reset to allow retry
      if (this.mounted) {
        this.setState({ isConverting: false });
      }
    }

    // Skip if already converting and we have converted data (prevents duplicate conversions)
    if (this.state.isConverting && this.state.convertedData) {
      return;
    }

    this.mounted && this.setState({ isConverting: true });

    try {
      const { convertHeicToJpeg } = await import("utils/helpers/heicToJpegHelper");
      const convertedData = await convertHeicToJpeg(data);

      if (this.mounted) {
        this.setState(
          {
            convertedData,
            convertedContentType: FileEnums.ContentTypes.ImageJPEG,
            isConverting: false,
          },
          () => {
            // Callback ensures state is updated before calculating dimensions
            if (this.mounted) {
              this.setImageWidthHeight();
            }
          }
        );
      }
    } catch (error) {
      console.error("Failed to convert HEIC image:", error);
      if (this.mounted) {
        this.setState({ isConverting: false });
      }
    }
  };

  getDisplayImageData = () => {
    const { contentType, data } = this.props.currentFile;
    return {
      contentType: this.state.convertedContentType || contentType,
      data: this.state.convertedData || data,
    };
  };

  setImageWidthHeight = () => {
    const { contentType, data } = this.getDisplayImageData();

    const src = data
      ? `data:${contentType};base64,${data}`
      : NoImage;

    const { containerWidth: frameWidth, containerHeight: frameHeight } = this.props;
    this.getImageDimensions(src).then((data) => {
      if (!this.props.fullScreen && this.context?.setUploadedFileDimensions) {
        this.context.setUploadedFileDimensions({ width: data.w, height: data.h });
      }
      let calculatedHeight = frameHeight;
      let calculatedWidth = frameWidth;

      if (this.props.fullScreen && data.w > data.h) {
        calculatedHeight = (data.h * frameWidth) / data.w;
      } else {
        if (frameHeight >= frameWidth) {
          calculatedWidth = frameWidth;
          calculatedHeight = (data.h * frameWidth) / data.w;
        } else if (frameHeight < frameWidth) {
          calculatedWidth = (data.w * frameHeight) / data.h;
          calculatedHeight = frameHeight;
        }
      }

      this.mounted &&
        this.setState({
          currentWidth: calculatedWidth,
          currentHeight: calculatedHeight,
        });
    });
  };

  getImageDimensions = (file: string) => {
    return new Promise<{ w: number; h: number }>(function (resolved, rejected) {
      const i = new Image();
      i.onload = function () {
        resolved({ w: i.width, h: i.height });
      };
      i.src = file;
    });
  };

  render() {
    const { contentType, data } = this.getDisplayImageData();

    return !this.props.isLoading &&
      data &&
      !this.state.isConverting &&
      this.state.currentHeight &&
      this.state.currentWidth ? (
      <img
        src={`data:${contentType};base64,${data}`}
        width={this.state.currentWidth}
        height={this.state.currentHeight}
      />
    ) : (
      <></>
    );
  }
}

export default FileViewerImage;
